import {
  MAX_TRANSITION_SOUND_VOLUME,
  TransitionSound,
} from "@repo/api/transition";
import {
  getTransitionSoundVolume,
  TRANSITION_SOUNDS,
} from "@repo/video-engine-i2v/components/TransitionSound/transition-sounds";
import { Fragment, useEffect, useRef, useState } from "react";
import {
  IconCheck,
  IconPlayerStopFilled,
  IconPlayerPlayFilled,
  IconVolumeOff,
} from "@tabler/icons-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownSelectTrigger,
} from "@/lib/components/DropdownMenu";
import { Slider } from "@/lib/components/Slider";
import { cn } from "@/lib/utils";

const soundLabels: Record<TransitionSound, string> = {
  FAST_AIR_SWOOSH: "Whoosh 1",
  SOFT_CINEMATIC_WHOOSH: "Whoosh 2",
  SWOOSH_1: "Whoosh 3",
  SHORT_SOFT_WHOOSH: "Whoosh 4",
  SOFT_WHOOSH: "Whoosh 5",
  SHORT_SOFT_TRANSITION: "Whoosh 6",
  ELEGANT_RISER: "Riser 1",
  SOFT_RISER_WHOOSH: "Riser 2",
  EPIC_CINEMATIC_BOOM: "Impact 1",
  EPIC_CINEMATIC_BOOM_2: "Impact 2",
  CINEMATIC_BASS_HIT: "Impact 3",
  CINEMATIC_IMPACT: "Impact 4",
  MAGICAL_REVEAL: "Magic 1",
  ARCANE_CHIME: "Magic 2",
  MYSTICAL_AMBIENCE: "Magic 3",
  CAMERA_FLASH: "Camera 1",
  CAMERA_SHUTTER: "Camera 2",
  VINTAGE_FILM_CAMERA: "Camera 3",
  SOFT_TICKING: "Ticking 1",
  CLEAN_TICKING: "Ticking 2",
  CLEAN_UI_WHOOSH: "Interface 1",
  CLEAN_TECH_WHOOSH: "Interface 2",
  SUBTLE_FUTURISTIC_BLIP: "Blip 1",
  SHORT_GLITCH: "Glitch 1",
  DIGITAL_GLITCH_SWIPE: "Glitch 2",
};

type Props = {
  value: TransitionSound | undefined;
  recommendedSounds: TransitionSound[];
  transitionLabel: string | undefined;
  onChange: (sound: TransitionSound | undefined) => void;
  volume: number;
  onVolumeChange: (volume: number) => void;
};

export default function TransitionSoundSelect({
  value,
  recommendedSounds,
  transitionLabel,
  onChange,
  volume,
  onVolumeChange,
}: Props) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const isKeyboardNavRef = useRef(false);
  const [draftVolume, setDraftVolume] = useState<number | null>(null);
  const [playingSound, setPlayingSound] = useState<TransitionSound | null>(
    null,
  );
  const volumePercent = draftVolume ?? Math.round(volume * 100);

  useEffect(() => () => audioRef.current?.pause(), []);

  const stopSound = () => {
    audioRef.current?.pause();
    audioRef.current = null;
    setPlayingSound(null);
  };

  const playSound = (sound: TransitionSound) => {
    audioRef.current?.pause();
    const audio = new Audio(TRANSITION_SOUNDS[sound].src);
    // HTMLAudioElement.volume can't go above 1; the render can.
    audio.volume = Math.min(
      1,
      getTransitionSoundVolume(sound) * (volumePercent / 100),
    );
    audio.onended = () => {
      if (audioRef.current === audio) stopSound();
    };
    audioRef.current = audio;
    setPlayingSound(sound);
    void audio.play();
  };

  const otherSounds = (Object.keys(soundLabels) as TransitionSound[]).filter(
    (sound) => !recommendedSounds.includes(sound),
  );

  const sections: { label?: string; sounds: TransitionSound[] }[] =
    recommendedSounds.length > 0
      ? [
          {
            label: transitionLabel
              ? `Suggested for ${transitionLabel}`
              : "Suggested",
            sounds: recommendedSounds,
          },
          { label: "All sounds", sounds: otherSounds },
        ]
      : [{ sounds: otherSounds }];

  return (
    <DropdownMenu onOpenChange={() => (isKeyboardNavRef.current = false)}>
      <DropdownSelectTrigger
        iconClassName="rotate-90"
        className={cn("px-4 font-medium", !value && "text-text-secondary")}
      >
        <span>{value ? soundLabels[value] : "No sound effect"}</span>
      </DropdownSelectTrigger>
      <DropdownMenuContent
        align="start"
        onKeyDown={() => (isKeyboardNavRef.current = true)}
        onPointerMove={() => (isKeyboardNavRef.current = false)}
        className="flex max-h-96 w-(--radix-dropdown-menu-trigger-width) flex-col overflow-hidden p-0"
      >
        <div className="overflow-y-auto p-1">
          <DropdownMenuItem
            role="menuitemradio"
            aria-checked={!value}
            onSelect={() => onChange(undefined)}
          >
            <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-grey-100 text-black-400">
              <IconVolumeOff className="size-3.5!" />
            </span>
            No sound effect
            {!value && (
              <IconCheck
                stroke={2.5}
                className="ml-auto stroke-[url(#icon-gradient)]"
              />
            )}
          </DropdownMenuItem>
          {sections.map((section) => (
            <Fragment key={section.label ?? "sounds"}>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                {section.label && (
                  <DropdownMenuLabel className="px-2 pt-2 pb-1 text-xs font-medium text-text-secondary">
                    {section.label}
                  </DropdownMenuLabel>
                )}
                {section.sounds.map((sound) => (
                  <SoundItem
                    key={sound}
                    sound={sound}
                    isSelected={sound === value}
                    onSelect={() => onChange(sound)}
                    isPlaying={sound === playingSound}
                    onPlay={() => playSound(sound)}
                    onStop={stopSound}
                    onFocus={() => {
                      if (isKeyboardNavRef.current) playSound(sound);
                    }}
                  />
                ))}
              </DropdownMenuGroup>
            </Fragment>
          ))}
        </div>
        <div className="flex h-12 shrink-0 items-center gap-3 border-t px-3">
          <div
            inert={!value}
            className={cn(
              "flex min-w-0 flex-1 items-center gap-2",
              !value && "opacity-40",
            )}
            onKeyDown={(e) => {
              if (e.key.startsWith("Arrow")) e.stopPropagation();
            }}
          >
            <span className="text-xs font-medium text-text-secondary">
              Volume
            </span>
            <div className="min-w-0 flex-1">
              <Slider
                value={volumePercent}
                onValueChange={setDraftVolume}
                onValueCommit={(percent) => {
                  setDraftVolume(null);
                  onVolumeChange(percent / 100);
                }}
                min={0}
                max={MAX_TRANSITION_SOUND_VOLUME * 100}
                step={1}
              />
            </div>
            <span className="w-9 text-right text-xs text-text-secondary tabular-nums">
              {volumePercent}%
            </span>
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

type SoundItemProps = {
  sound: TransitionSound;
  isSelected: boolean;
  onSelect: () => void;
  isPlaying: boolean;
  onPlay: () => void;
  onStop: () => void;
  onFocus: () => void;
};

function SoundItem({
  sound,
  isSelected,
  onSelect,
  isPlaying,
  onPlay,
  onStop,
  onFocus,
}: SoundItemProps) {
  return (
    <DropdownMenuItem
      role="menuitemradio"
      aria-checked={isSelected}
      onSelect={onSelect}
      onFocus={onFocus}
    >
      <button
        type="button"
        tabIndex={-1}
        aria-label={`${isPlaying ? "Stop" : "Play"} ${soundLabels[sound]}`}
        className="flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-full bg-grey-100 text-black-400 transition-colors hover:bg-grey-200"
        // Keep the click on the play button from selecting the item.
        onPointerDown={(e) => e.stopPropagation()}
        onPointerUp={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          if (isPlaying) onStop();
          else onPlay();
        }}
      >
        {isPlaying ? (
          <IconPlayerStopFilled className="size-3!" />
        ) : (
          <IconPlayerPlayFilled className="size-3!" />
        )}
      </button>
      {soundLabels[sound]}
      {isSelected && (
        <IconCheck
          stroke={2.5}
          className="ml-auto stroke-[url(#icon-gradient)]"
        />
      )}
    </DropdownMenuItem>
  );
}
